import dayjs from "dayjs";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { isPeriodPreset, type PeriodPreset } from "../utils/period";

export interface ReportFilterState {
	dateFrom: string;
	dateTo: string;
	employeeId?: string;
	departmentId?: string;
	zoneId?: string;
	/**
	 * Tanlangan davr segmenti — FAQAT UI holati.
	 *
	 * ⚠️ `buildReportParams` ga TUSHMAYDI: server uni bilmaydi va
	 * `ReportListQuerySchema` qat'iy bo'lmasa ham so'rov kaliti/eksport
	 * parametri ortiqcha maydon bilan ifloslanardi. Haqiqat — sanalar.
	 */
	preset?: PeriodPreset;
}

export interface ReportDateRange {
	dateFrom: string;
	dateTo: string;
}

/** Tartib BARQAROR — URL har renderda almashib turmasligi uchun. */
const ID_KEYS = ["employeeId", "departmentId", "zoneId"] as const;

const SANA_SHAKLI = /^\d{4}-\d{2}-\d{2}$/;

/**
 * ⚠️ **Kalendar tekshiruvi — shakl tekshiruvidan tashqari.**
 * `2026-02-30` shaklga MOS keladi, lekin backend `sanaSchema` uni 422
 * bilan rad etadi (`reports.schemas.ts` dagi round-trip qorovuli).
 * URL qo'lda tahrirlanishi mumkin, ya'ni buzuq qiymat shu yerda
 * to'xtatilmasa foydalanuvchi sababsiz xato olardi.
 */
function haqiqiySana(qiymat: string | null): qiymat is string {
	if (!(qiymat && SANA_SHAKLI.test(qiymat))) {
		return false;
	}

	return dayjs(qiymat, "YYYY-MM-DD").format("YYYY-MM-DD") === qiymat;
}

/**
 * Standart oraliq — joriy oyning boshidan bugungacha.
 *
 * ⚠️ `now` PARAMETR: funksiya ichida vaqt hech qachon o'qilmaydi, aks
 * holda testda oy chegarasida flaky bo'lardi (`runShiftGeneration`
 * naqshi).
 */
export function defaultReportRange(now: Date): ReportDateRange {
	const kun = dayjs(now);

	return {
		dateFrom: kun.startOf("month").format("YYYY-MM-DD"),
		dateTo: kun.format("YYYY-MM-DD"),
	};
}

/**
 * ⚠️ Sana oralig'i HAR DOIM to'liq: yo'q yoki buzuq qiymat zaxiraga
 * tushadi. `useReportRows` ning `enabled` i shu bilan doim rost
 * bo'ladi va foydalanuvchi bo'sh sahifa o'rniga ma'lumot ko'radi.
 */
export function parseReportFilters(search: string, fallback: ReportDateRange): ReportFilterState {
	const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	const dateFrom = params.get("dateFrom");
	const dateTo = params.get("dateTo");

	const filters: ReportFilterState = {
		dateFrom: haqiqiySana(dateFrom) ? dateFrom : fallback.dateFrom,
		dateTo: haqiqiySana(dateTo) ? dateTo : fallback.dateTo,
	};

	for (const key of ID_KEYS) {
		const value = params.get(key);

		if (value) {
			filters[key] = value;
		}
	}

	// ⚠️ Noma'lum preset JIMGINA tashlanadi — qo'lda tahrirlangan URL
	// segmentni buzmasin.
	const preset = params.get("preset");

	if (isPeriodPreset(preset)) {
		filters.preset = preset;
	}

	return filters;
}

export function serializeReportFilters(filters: ReportFilterState): string {
	const params = new URLSearchParams();

	params.set("dateFrom", filters.dateFrom);
	params.set("dateTo", filters.dateTo);

	for (const key of ID_KEYS) {
		const value = filters[key];

		if (value) {
			params.set(key, value);
		}
	}

	if (filters.preset) {
		params.set("preset", filters.preset);
	}

	return params.toString();
}

/**
 * Filtrlarni so'rov parametrlariga aylantiradi.
 *
 * ⚠️ **Jadval, eksport va chop etish UCHALASI shu funksiyadan o'tadi**
 * (`buildLeadParams` naqshi) — uch nusxa bo'lsa «eksportda hudud
 * filtri ishlamaydi» sinfidagi jimgina tafovut paydo bo'lardi.
 */
export function buildReportParams(filters: ReportFilterState): Record<string, string> {
	const params: Record<string, string> = {
		dateFrom: filters.dateFrom,
		dateTo: filters.dateTo,
	};

	for (const key of ID_KEYS) {
		const value = filters[key];

		if (value) {
			params[key] = value;
		}
	}

	return params;
}

/**
 * ⚠️ **Nima uchun TanStack Router ning `useSearch()` i emas.**
 * `routeTree.tsx` — poydevor fayli va H bloki unga faqat bitta bola
 * marshrut qo'shadi (kontrakt §0), ya'ni `validateSearch` yozilmaydi;
 * usiz `useSearch()` tiplanmagan `{}` qaytaradi. `useLeadFilters`
 * bilan bir xil naqsh: History API + maxsus hodisa, sof
 * serializatsiya funksiyalari esa alohida test qilinadi.
 *
 * ⚠️ `replaceState` — `pushState` EMAS: har filtr o'zgarishi tarixga
 * yozilsa «orqaga» tugmasi sahifadan chiqarish o'rniga o'nlab filtr
 * holatidan orqaga surardi.
 */
const FILTER_EVENT = "skud:report-filters";

function subscribe(onChange: () => void): () => void {
	window.addEventListener("popstate", onChange);
	window.addEventListener(FILTER_EVENT, onChange);

	return () => {
		window.removeEventListener("popstate", onChange);
		window.removeEventListener(FILTER_EVENT, onChange);
	};
}

export function useReportFilters(): {
	filters: ReportFilterState;
	setFilters: (next: ReportFilterState) => void;
} {
	const search = useSyncExternalStore(
		subscribe,
		() => window.location.search,
		// SSR yo'q, lekin `useSyncExternalStore` server snapshot'ini talab
		// qiladi va uni tashlab ketish React ogohlantirishini beradi.
		() => ""
	);

	// ⚠️ Zaxira oraliq render paytida BIR MARTA hisoblanadi va
	// `search` o'zgarganda qayta ishlatiladi: har renderda `new Date()`
	// chaqirilsa, yarim tunda so'rov kaliti o'zgarib jadval o'z-o'zidan
	// qayta yuklanardi.
	const fallback = useMemo(() => defaultReportRange(new Date()), []);
	const filters = useMemo(() => parseReportFilters(search, fallback), [search, fallback]);

	const setFilters = useCallback((next: ReportFilterState) => {
		const qs = serializeReportFilters(next);

		window.history.replaceState(null, "", `${window.location.pathname}?${qs}`);
		window.dispatchEvent(new Event(FILTER_EVENT));
	}, []);

	return { filters, setFilters };
}
